/**
 * Reviews masonry — /pages/reviews (page.all-reviews.json), DEV v561-v563.
 *
 * Klaviyo's All Reviews widget renders a flat list of .kl_reviews__review_item
 * tiles inside an unclassed wrapper. Pure CSS multi-column layout balances
 * badly with unsplittable tiles (3rd column ends short), so we lay the wrapper
 * out as a CSS grid with tiny implicit rows and place every tile explicitly
 * (shortest column first, row-span from its measured height). Klaviyo's DOM is never reordered or moved
 * (React-safe); only inline grid-column/grid-row are written on each tile.
 * Source order is preserved for screen readers.
 *
 * Companion CSS: assets/klaviyo-reviews.css → `.reviews-masonry` rules.
 */
(() => {
  const WRAPPER = '#klaviyo-all-reviews-wrapper';
  const ITEM = '.kl_reviews__review_item';
  const ROW_PX = 4; // must match grid-auto-rows in klaviyo-reviews.css
  const GAP_PX = 24; // must match .kl_reviews__review_item margin-bottom

  let grid = null;
  let resizeObserver = null;

  let frame = 0;

  // Shortest-column-first placement, recomputed for all tiles in source order.
  const layoutAll = () => {
    frame = 0;
    if (!grid) return;
    const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').length || 1;
    const heights = new Array(cols).fill(0); // in grid rows
    grid.querySelectorAll(`:scope > ${ITEM}`).forEach((item) => {
      const height = item.getBoundingClientRect().height;
      if (!height) return;
      const span = Math.ceil((height + GAP_PX) / ROW_PX);
      let col = 0;
      for (let i = 1; i < cols; i++) if (heights[i] < heights[col]) col = i;
      item.style.gridColumn = String(col + 1);
      item.style.gridRow = `${heights[col] + 1} / span ${span}`;
      heights[col] += span;
    });
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(layoutAll);
  };

  const observeItems = () => {
    if (!grid) return;
    grid.querySelectorAll(`:scope > ${ITEM}`).forEach((item) => {
      if (item.dataset.masonry !== '1') {
        item.dataset.masonry = '1';
        resizeObserver.observe(item);
      }
    });
    schedule();
  };

  const attach = () => {
    const wrapper = document.querySelector(WRAPPER);
    const firstItem = wrapper && wrapper.querySelector(ITEM);
    const candidate = firstItem && firstItem.parentElement;
    if (!candidate) return false;
    if (candidate !== grid) {
      grid = candidate;
      grid.classList.add('reviews-masonry');
      if (resizeObserver) resizeObserver.disconnect();
      resizeObserver = new ResizeObserver(schedule);
    }
    observeItems();
    return true;
  };

  if (!('ResizeObserver' in window)) return; // CSS column fallback stays in effect

  // Klaviyo hydrates late and re-renders on filter/sort/"Show more".
  new MutationObserver(attach).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', schedule);
  attach();
})();
